import {
  RestoreNoTransactionError,
  classifyPurchaseError,
  failureOf,
  createSubscribeFlow,
  withFailure,
  type PurchasePresenter,
  readOwnerUid,
} from '@forest/paywall-core'
import isNetworkError from 'is-network-error'
import { isHTTPError, isTimeoutError } from 'ky'
import { useGatedMutation } from '~/lib/gated-mutation'
import { AbortError, getAuthenticatedUserInfo } from '~/lib/get-user-info'
import { openIntercom } from '~/lib/intercom'
import { logNonFatalError } from '~/lib/log-errors'
import { posthog } from '~/lib/posthog'
import { m } from '~/paraglide/messages'
import { platform } from '~/params'
import { api } from '~/services/api'
import { native } from '@forest/native-services'
import { dialog } from '@forest/shared/react'
import { loadingOverlay } from '~/store/loading-overlay'
import { $receiptSystemAccountId } from '~/store/receipt-system-account-id'
import { reminderDialog } from '~/store/reminder-dialog'
import type { ProductMetadata } from '~/types/iap'
import { showSubscriptionConflictDialog } from '~/lib/subscription-conflict-dialog'

const flow = createSubscribeFlow<ProductMetadata>({
  api,
  platform,
  native: {
    purchase: native.iap.purchase,
    closeTransaction: native.iap.closeTransaction,
    getAllUnclosedTransactions: native.iap.getAllUnclosedTransactions,
    getAllEntitlements: native.iap.getAllEntitlements,
    logEvent: native.general.logEvent,
    refreshServiceLevel: () => native.user.refreshServiceLevel(),
  },
  telemetry: {
    captureException: (error, context) => {
      logNonFatalError({
        name: 'subscription_error',
        code: 1,
        params: {
          step: context.step,
          failure_code: context.failure_code,
          purchase_id: context.purchase_id?.toString() ?? '',
          transaction_id: context.transaction_id ?? '',
          product_sku: context.product_sku,
          message: String(error),
        },
      })
      posthog?.captureException(error, context)
    },
  },
  getUserId: async () => (await getAuthenticatedUserInfo()).user_id,
  getReceiptSystemAccountId: () => $receiptSystemAccountId.get(),
})

const presenter: PurchasePresenter<ProductMetadata> = {
  success: () => {},
  error: (intent, { flow }) => {
    switch (intent.kind) {
      case 'payment_timeout':
        if (intent.detail === 'interrupted') return
        dialog.open({
          title: m.dialog_payment_timeout_title(),
          description: withFailure(
            m.dialog_payment_timeout_content({ error: intent.detail }),
            intent.failure,
          ),
          button: m.OK(),
        })
        return
      case 'illegal_transaction_restore':
        dialog.open({
          title: m.dialog_illegal_transaction_title(),
          description: withFailure(
            platform === 'china_android'
              ? m.dialog_illegal_transaction_content_cna()
              : m.dialog_illegal_transaction_content(),
            intent.failure,
          ),
          button: m.restore_purchase(),
          closeButton: true,
          asyncAction: async () => {
            try {
              await flow.restore()
              dialog.close()
              dialog.open({
                title: m.dialog_restore_success_title(),
                description: m.dialog_restore_success_content(),
                button: m.awesome(),
                action: () => {
                  native.user.refreshServiceLevel()
                  native.general.close()
                },
              })
            } catch (restoreError) {
              if (restoreError instanceof AbortError) {
                dialog.close()
                return
              }
              dialog.close()
              await showRestoreErrorDialog(restoreError)
            }
          },
        })
        return
      case 'no_internet':
        dialog.open({
          title: m.dialog_connection_issue_title(),
          description: withFailure(
            m.dialog_connection_issue_description(),
            intent.failure,
          ),
          button: m.OK(),
        })
        return
      case 'http_error':
        dialog.open({
          title: m.dialog_unknown_error_title(),
          description: withFailure(
            m.dialog_unknown_error_content({
              error_code: intent.failure.code,
            }),
            intent.failure,
            { codeInDescription: true },
          ),
          button: m.contact_cs(),
          action: () => {
            openIntercom({
              node: 'subscribe_create_purchase_unexpected_http_error',
              orderId: intent.failure.purchaseId?.toString(),
              additional: { error_code: intent.failure.code },
            })
          },
        })
        return
      case 'subscription_conflict':
        void showSubscriptionConflictDialog(intent.ownerUid, intent.failure)
        return
      case 'unknown':
        dialog.open({
          title: m.dialog_unknown_error_title(),
          description: withFailure(
            m.dialog_unknown_error_content({
              error_code: intent.failure.code,
            }),
            intent.failure,
            { codeInDescription: true },
          ),
          button: m.contact_cs(),
          action: () => {
            openIntercom({
              node: 'subscribe_create_purchase_unexpected_error',
              orderId: intent.failure.purchaseId?.toString(),
              additional: { error_code: intent.failure.code },
            })
          },
        })
        return
      default:
        intent satisfies never
    }
  },
}

export function useSubscribeMutation(params?: { onSuccess?: () => void }) {
  const { onSuccess } = params ?? {}

  return useGatedMutation({
    mutationFn: async ({
      product,
      isTrial,
    }: {
      product: ProductMetadata
      isTrial: boolean
    }) => {
      loadingOverlay.show()
      const status = await flow.run(product)
      return { status, isTrial, product }
    },

    onSettled: () => {
      loadingOverlay.hide()
    },

    onSuccess: async ({ status, isTrial, product }) => {
      presenter.success(flow.handleSuccess(status, product))

      const isSubscriptionActive = status?.service_level === 'subscription'
      if (isTrial && isSubscriptionActive) {
        const hasPermission = await native.permission
          .getHasNotificationPermission()
          .then((r) => r.success && r.data)
          .catch(() => false)

        if (!hasPermission && status?.subscription?.expires_at) {
          const expiresAt = new Date(status.subscription.expires_at)
          const trialEndDate = expiresAt.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
          await reminderDialog.open(trialEndDate)
        }
      }

      onSuccess?.()
    },

    onError: (error) => {
      if (error instanceof AbortError) return
      presenter.error(classifyPurchaseError(error), { error, flow })
    },
  })
}

async function showRestoreErrorDialog(error: unknown) {
  const failure = failureOf(error, 'restore')
  if (error instanceof RestoreNoTransactionError) {
    dialog.open({
      title: m.dialog_restore_error_title(),
      description: withFailure(
        platform === 'china_android'
          ? m.dialog_restore_no_transaction_content_cna()
          : m.dialog_restore_no_transaction_content({
              app_store_account:
                platform === 'ios' ? m.apple_id() : m.google_play_store_id(),
            }),
        failure,
      ),
      button: m.OK(),
    })
    return
  }

  if (isHTTPError(error)) {
    const status = error.response.status
    if ((status === 409 || status === 412) && platform !== 'china_android') {
      await showSubscriptionConflictDialog(await readOwnerUid(error), failure)
      return
    }
    if (status === 403 && platform !== 'china_android') {
      dialog.open({
        title: m.dialog_restore_error_title(),
        description: withFailure(
          m.dialog_restore_error_forest_account_content({
            app_store_account:
              platform === 'ios' ? m.apple_id() : m.google_play_store_id(),
          }),
          failure,
        ),
        button: m.OK(),
      })
      return
    }
    if (
      status === 410 ||
      ((status === 403 || status === 409) && platform === 'china_android')
    ) {
      dialog.open({
        title: m.dialog_transaction_expired_title(),
        description: withFailure(
          m.dialog_transaction_expired_content(),
          failure,
        ),
        button: m.OK(),
      })
      return
    }
    dialog.open({
      title: m.dialog_restore_error_title(),
      description: withFailure(
        m.dialog_unknown_error_content({ error_code: failure.code }),
        failure,
        { codeInDescription: true },
      ),
      button: m.contact_cs(),
      action: () => {
        openIntercom({
          node: 'restore_purchase_unexpected_http_error',
          orderId: failure.purchaseId?.toString(),
          additional: { error_code: failure.code },
        })
      },
    })
    return
  }

  if (isNetworkError(error) || isTimeoutError(error)) {
    dialog.open({
      title: m.dialog_connection_issue_title(),
      description: withFailure(
        m.dialog_connection_issue_description(),
        failure,
      ),
      button: m.OK(),
    })
    return
  }

  dialog.open({
    title: m.dialog_restore_error_title(),
    description: withFailure(
      m.dialog_unknown_error_content({ error_code: failure.code }),
      failure,
      { codeInDescription: true },
    ),
    button: m.contact_cs(),
    action: () => {
      openIntercom({
        node: 'restore_purchase_unexpected_error',
        orderId: failure.purchaseId?.toString(),
        additional: { error_code: failure.code },
      })
    },
  })
}
